import { useState, useEffect, useMemo } from 'react';
import { normalizeStatus } from '../utils/configItemLifecycle';
import { formatDateRange } from '../utils/formatDate';
import { useFirmCapabilities, CAPABILITY_ATLASSIAN_UI } from '../hooks/useFirmCapabilities';
import { useFocusedEngagement } from '../hooks/useFocusedEngagement';
import { EngagementSwitcher } from './EngagementSwitcher';
import { ScopeSignalsPanel } from './ScopeSignalsPanel';
import {
  Loader2,
  AlertCircle,
  FileText,
  Plus,
  Share2,
  Layers,
  MoreHorizontal,
  Sparkles,
  Trash2,
  Settings,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { ClientDetailHeader } from './ClientDetailHeader';
import { ClientSpacesOverview } from './ClientSpacesOverview';
import { ClientConfigItemsTable } from './ClientConfigItemsTable';
import { ClientActivityTimeline } from './ClientActivityTimeline';
import { EngagementInsightsStep } from './EngagementInsightsStep';
import { ExportWizard } from './ExportWizard';
import { ImplementationDocumentation } from './ImplementationDocumentation';
import { BuildBacklog } from './BuildBacklog';
import { WinsKudosTab } from './WinsKudosTab';
import { JiraBacklogGenerator } from './JiraBacklogGenerator';
import { AssetConfiguration } from './AssetConfiguration';
import { TopNavigation } from './TopNavigation';
import { SOWDetailsModal } from './SOWDetailsModal';
import { AddSOWDialog, type SOWCreatedInfo } from './AddSOWDialog';
import { ScopeReconcileDialog } from './ScopeReconcileDialog';
import { RECONCILE_AFTER_SOW_INTRO, reconcileOfferFor, type ReconcileOffer } from '../utils/reconcileAfterSow';
import { CIApprovalModal } from './CIApprovalModal';
import { QuickCompanySettingsDialog } from './QuickCompanySettingsDialog';
import { InviteClientUserDialog } from './InviteClientUserDialog';
import { SOWGenerator } from './SOWGenerator';
import { SOWVersionHistory } from './SOWVersionHistory';
import { ShareSOWDialog } from './ShareSOWDialog';
import { AnalyzeSOWDialog } from './AnalyzeSOWDialog';
import { CompletenessTracker } from './CompletenessTracker';
import { ClientStatusNoteCard } from './ClientStatusNoteCard';
import { EngagementOverview } from './EngagementOverview';
import { resolveCategory } from '../utils/guidedCategories';
import { SpaceManagementPanel } from './SpaceManagementPanel';
import { ScopeControlCenter } from './ScopeControlCenter';
import { EngagementWorkspace } from './EngagementWorkspace';
import { CompanySettings } from './CompanySettings';
import { CompanyDocuments } from './CompanyDocuments';
import { UserImpersonationDialog } from './UserImpersonationDialog';
import { EngagementCatalog } from './EngagementCatalog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { projectId } from '../utils/supabase/info';
import type { HierarchicalConfigItem } from '../types/phase1e';
import { toast } from 'sonner';
import { formatCategoryLabel, normalizeAcronyms } from '../utils/normalizeAcronyms';

interface ClientDetailViewProps {
  companyId: string;
  accessToken: string;
  currentUser: any;
  onBack: () => void;
  onLogout: () => void;
  onNavigateToSpace?: (companyId: string, spaceId: string) => void;
}

interface ClientSummary {
  company: {
    id: string;
    name: string;
    logo?: string;
  };
  totalItems: number;
  completedItems: number;
  waitingForConsultant: number;
  waitingForClient: number;
  spaces: number;
  users: number;
  byCategory: Record<string, any>;
  bySpace: Record<string, any>;
  recentActivity: any[];
  scopeMetrics?: {
    undecided: number;
    changeOrder: number;
  };
  strategicContext?: {
    hlos: string[];
    salesNotes: string[];
    signedScope: string[];
    notContracted: string[];
    scopeSignals?: any[];
    hloAlignment?: {
      score: number;
      hloCoverage: Array<{
        hloId: string;
        title: string;
        category: string;
        coverage: 'covered' | 'partial' | 'uncovered';
        matchedScopeItems: any[];
        gaps: string[];
      }>;
      summary: { covered: number; partial: number; uncovered: number; total: number };
    } | null;
  };
}

export function ClientDetailView({ companyId, accessToken, currentUser, onBack, onLogout, onNavigateToSpace }: ClientDetailViewProps) {
  const consultingFirmId = currentUser?.firmId || currentUser?.consultingFirmId;
  const { hasCapability, ready: capabilitiesReady } = useFirmCapabilities(consultingFirmId, accessToken);
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [configItems, setConfigItems] = useState<HierarchicalConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sows, setSOWs] = useState<any[]>([]);
  const [sowsLoading, setSOWsLoading] = useState(true);
  // Focused engagement (PRD §4.7): URL → per-user preference → only → latest active → latest created
  const {
    focusedEngagementId,
    setFocusedEngagement,
    hasMultiple: hasMultipleEngagements,
  } = useFocusedEngagement({ engagements: sows, companyId, userId: currentUser?.id });
  const [addSOWDialogOpen, setAddSOWDialogOpen] = useState(false);
  // Spec §4.10: a SOW added to a company that already had an engagement opens the reconcile step when Add SOW closes.
  const [createdSOW, setCreatedSOW] = useState<SOWCreatedInfo | null>(null);
  const [reconcileOffer, setReconcileOffer] = useState<ReconcileOffer | null>(null);
  const [sowDetailsModalOpen, setSOWDetailsModalOpen] = useState(false);
  const [selectedSOWId, setSelectedSOWId] = useState<string | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<any>(null);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [ciApprovalModalOpen, setCIApprovalModalOpen] = useState(false);
  const [quickCompanySettingsDialogOpen, setQuickCompanySettingsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  // Company documents the viewer has not opened yet (the Documents tab's badge; RESTORE_PLAN R3.4)
  const [unviewedDocs, setUnviewedDocs] = useState(0);
  useEffect(() => {
    let live = true;
    fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/documents/unviewed-count`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live && d) setUnviewedDocs(Number(d.unviewedCount) || 0); })
      .catch(() => {});
    return () => { live = false; };
  }, [companyId, accessToken]);
  const [showInsights, setShowInsights] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [showWins, setShowWins] = useState(false);
  const [showJira, setShowJira] = useState(false);
  const [showAssets, setShowAssets] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [ballInCourtFilter, setballInCourtFilter] = useState<string | undefined>(undefined); // NEW: Ball in court filter
  const [scopeControlFilter, setScopeControlFilter] = useState<string | undefined>(undefined);
  const [sowGeneratorOpen, setSOWGeneratorOpen] = useState(false);
  const [engagementCatalogOpen, setEngagementCatalogOpen] = useState(false);
  const [sowVersionHistoryOpen, setSOWVersionHistoryOpen] = useState(false);
  const [shareSOWDialogOpen, setShareSOWDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  // Edit SOW capability removed: executed SOWs are legal contracts. To
  // amend scope, use a change order; to fix a wrong upload, delete the
  // SOW and re-upload.
  const [analyzeSOWDialogOpen, setAnalyzeSOWDialogOpen] = useState(false);
  const [deleteSOWConfirmOpen, setDeleteSOWConfirmOpen] = useState(false);
  const [deletingSOWId, setDeletingSOWId] = useState<string | null>(null);
  const [deletingSOW, setDeletingSOW] = useState(false);
  // Company-level AI preference gates the Analyze action on each SOW card.
  // Loaded once at the detail-view level (not per-card) to avoid an N+1
  // preference fetch. Same source of truth as AddSOWDialog's pre-analyze
  // gating: GET /companies/:id/preferences -> preferences.aiEnabled, default
  // true. Treat only explicit false as disabled so a transient pref fetch
  // failure does not silently strip the Analyze action.
  const [companyAiEnabled, setCompanyAiEnabled] = useState<boolean>(true);
  const [companyTemplate, setCompanyTemplate] = useState<string | null>(null);
  const [targetConfigItemId, setTargetConfigItemId] = useState<string | undefined>(undefined);
  const [initialGridCellId, setInitialGridCellId] = useState<string | null>(null);
  const [targetConfigItemTab, setTargetConfigItemTab] = useState<string | undefined>(undefined);
  const [changeRequests, setChangeRequests] = useState<any[]>([]);
  const [userImpersonationDialogOpen, setUserImpersonationDialogOpen] = useState(false);

  // Sync state with URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scopeFilter = params.get('scopeFilter');
    if (scopeFilter) {
      setScopeControlFilter(scopeFilter);
      setActiveTab('scope-controls');
    }
    // Discovery grid deep links (DISCOVERY_GRID_REDESIGN_SPEC §4.8): ?tab=config-items&cell=<id>
    // from the digest / guidance panel, or ?configItem=<id> from the change-order summary.
    const tab = params.get('tab');
    const cell = params.get('cell');
    const configItem = params.get('configItem');
    if (tab === 'config-items' || cell || configItem) setActiveTab('config-items');
    if (cell) setInitialGridCellId(cell);
    if (configItem) setTargetConfigItemId(configItem);
  }, []);

  // Update URL when scope filter changes
  useEffect(() => {
    if (activeTab === 'scope-controls' && scopeControlFilter) {
      const url = new URL(window.location.href);
      url.searchParams.set('scopeFilter', scopeControlFilter);
      window.history.replaceState({}, '', url.toString());
    } else {
        // Optional: clear param if leaving tab?
        // Let's keep it simple for now.
    }
  }, [activeTab, scopeControlFilter]);

  useEffect(() => {
    loadClientSummary('initial');
    loadConfigItems();
    loadSOWs();
    loadPendingApprovals();
    loadCompanyTemplate();
    loadChangeRequests();
    loadCompanyAiPref();
  }, [companyId]);

  // Fetches the company's AI preference once per company. Mirrors the
  // optimistic default used in AddSOWDialog: stay on the AI-enabled path
  // unless the server explicitly says aiEnabled === false, so a transient
  // failure cannot silently remove the Analyze action from every SOW card.
  const loadCompanyAiPref = async () => {
    if (!companyId) return;
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/preferences`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) {
        setCompanyAiEnabled(true);
        return;
      }
      const data = await response.json();
      const prefs = data?.preferences || {};
      setCompanyAiEnabled(prefs.aiEnabled === false ? false : true);
    } catch {
      setCompanyAiEnabled(true);
    }
  };

  const handleDeleteSOWConfirmed = async () => {
    if (!deletingSOWId) return;
    setDeletingSOW(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${deletingSOWId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!response.ok) {
        let detail = `Delete failed (status ${response.status})`;
        try {
          const body = await response.json();
          detail = body?.error || detail;
        } catch {
          // keep status-only detail
        }
        toast.error(detail);
        return;
      }
      toast.success('SOW deleted');
      setDeleteSOWConfirmOpen(false);
      setDeletingSOWId(null);
      loadSOWs();
    } catch (err: any) {
      console.error('[ClientDetailView] SOW delete failed:', err);
      toast.error(err?.message || 'Failed to delete SOW');
    } finally {
      setDeletingSOW(false);
    }
  };

  const loadChangeRequests = async () => {
    try {
        const response = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/change-requests`,
            {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        if (response.ok) {
            const data = await response.json();
            setChangeRequests(data.changeRequests || []);
        }
    } catch (error) {
        console.error('Error loading change requests:', error);
    }
  };

  /**
   * Background mode ('refresh', default) MUST NOT toggle the full-page `loading`
   * flag. Toggling `loading` unmounts child panels (e.g. SpaceManagementPanel and
   * its CreateSpaceDialog) mid-render. Radix Dialog cleanup is interrupted by an
   * abrupt portal unmount, leaving a stuck overlay / scroll-lock and the
   * appearance that "Create Space" did not close. Only the initial mount path
   * should pass mode='initial'.
   */
  const loadClientSummary = async (mode: 'initial' | 'refresh' = 'refresh') => {
    try {
      if (mode === 'initial') setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/clients/${companyId}/summary`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error loading client summary:', error);
    } finally {
      if (mode === 'initial') setLoading(false);
    }
  };

  const loadConfigItems = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/config-items`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[ClientDetailView] Config items received:', data.items);
        if (data.items && data.items.length > 0) {
          console.log('[ClientDetailView] First item structure:', data.items[0]);
        }
        // Map items to ensure they have an 'id' field
        // Some data sources use 'itemId' or 'configItemId' instead
        const mappedItems = (data.items || []).map((item: any) => ({
          ...item,
          id: item.id || item.itemId || item.configItemId || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }));
        console.log('[ClientDetailView] Mapped items with IDs:', mappedItems.length > 0 ? mappedItems[0] : 'none');
        setConfigItems(mappedItems);
      }
    } catch (error) {
      console.error('Error loading config items:', error);
    }
  };

  const loadSOWs = async () => {
    try {
      setSOWsLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/sows`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSOWs(data.sows || []);
      } else {
        console.error('Failed to load SOWs:', response.statusText);
        toast.error('Failed to load SOWs');
      }
    } catch (error) {
      console.error('Error loading SOWs:', error);
      toast.error('Error loading SOWs');
    } finally {
      setSOWsLoading(false);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/pending-ci-approvals/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingApprovals(data.pendingApprovals);
        setPendingApprovalsCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    }
  };

  const loadCompanyTemplate = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/template`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[ClientDetailView] Company template data:', data);
        setCompanyTemplate(data.template || data.templateId || null);
      }
    } catch (error) {
      console.error('Error loading company template:', error);
    }
  };

  const handleUpdate = () => {
    loadClientSummary();
    loadConfigItems();
    loadSOWs();
    loadPendingApprovals();
    loadChangeRequests();
  };

  // Path guidance's "Start this step": the engine names a category key; the alias table maps it to
  // a category this client has (utils/guidedCategories.ts), shown on the config items tab.
  const startRecommendedStep = (hint: string) => {
    const category = resolveCategory(configItems, hint);
    if (!category) {
      toast.info('This client has no items for that step yet.');
      return;
    }
    setSelectedCategory(category);
    setActiveTab('config-items');
  };

  const handleNavigateToConfigItem = (configItemId: string, tab?: string) => {
    setTargetConfigItemId(configItemId);
    setTargetConfigItemTab(tab);
    setActiveTab('config-items');
  };

  const handleShowScopeControls = (filter?: string) => {
      if (filter) setScopeControlFilter(filter);
      setActiveTab('scope-controls');
  };

  const atlassianCompanyTemplate = useMemo(() => {
    if (!companyTemplate) return false;
    const template = companyTemplate.toLowerCase().trim();
    return (
      template.includes('atlassian') ||
      template.includes('jira') ||
      template.includes('jsm') ||
      template === 'atlassian_jsm'
    );
  }, [companyTemplate]);

  /**
   * Strict gating (Phase 3B prep): only render the Spaces experience once firm capabilities
   * have been confirmed by the server. While `capabilitiesReady` is false (idle/loading/error)
   * we render neither the entry point nor a "not available" placeholder, so we do not
   * (a) flash an Atlassian button that PR 2 will block at the API, or
   * (b) flash a denial on capable firms during the cap fetch.
   */
  const showSpacesExperience =
    capabilitiesReady && hasCapability(CAPABILITY_ATLASSIAN_UI) && atlassianCompanyTemplate;

  useEffect(() => {
    if (activeTab === 'spaces' && !showSpacesExperience) {
      setActiveTab('overview');
    }
  }, [activeTab, showSpacesExperience]);

  if (loading || !summary) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavigation
          user={{
            id: currentUser.id,
            name: currentUser.name || currentUser.email?.split('@')[0] || 'Consultant',
            email: currentUser.email,
            role: currentUser.role || 'consultant',
            avatarUrl: currentUser.avatarUrl,
            isDemo: currentUser.isDemo,
          }}
          companyId={null}
          companyName={null}
          accessToken={accessToken}
          onLogout={onLogout}
        >
          <div className="flex items-center justify-center py-4">
            <Loader2 className="size-6 animate-spin text-gray-400" />
          </div>
        </TopNavigation>
      </div>
    );
  }

  // Calculate metrics
  const blockedItems = configItems.filter(i => normalizeStatus(i) === 'blocked').length;
  const progressPercentage = summary.totalItems > 0 
    ? Math.round((summary.completedItems / summary.totalItems) * 100) 
    : 0;

  // Determine health status
  // UPDATED: More nuanced health status calculation
  // - At Risk: 25%+ of items are blocked (serious blocker problem)
  //   OR 50%+ of items waiting for client (client not engaged)
  // - Needs Attention: Progress < 30% (slow start)
  // - Healthy: Otherwise
  let healthStatus: 'healthy' | 'at-risk' | 'needs-attention' = 'healthy';
  const blockedPercentage = summary.totalItems > 0 ? (blockedItems / summary.totalItems) : 0;
  const clientWaitingPercentage = summary.totalItems > 0 ? (summary.waitingForClient / summary.totalItems) : 0;
  
  if (blockedPercentage >= 0.25 || clientWaitingPercentage > 0.5) {
    healthStatus = 'at-risk';
  } else if (progressPercentage < 30 && summary.totalItems > 0) {
    healthStatus = 'needs-attention';
  }

  // Format spaces for overview
  const spaces = Object.entries(summary.bySpace).map(([spaceId, data]: [string, any]) => ({
    id: spaceId,
    name: data.spaceName,
    type: data.spaceType,
    total: data.total,
    completed: data.completed,
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <TopNavigation
        user={{
          id: currentUser.id,
          name: currentUser.name || currentUser.email?.split('@')[0] || 'Consultant',
          email: currentUser.email,
          role: currentUser.role || 'consultant',
          avatarUrl: currentUser.avatarUrl,
          isDemo: currentUser.isDemo,
        }}
        companyId={null}
        companyName={null}
        accessToken={accessToken}
        onLogout={onLogout}
      >
        <div>
          <h1 className="text-xl font-bold">Client Engagement Details</h1>
          <p className="text-xs text-muted-foreground">{summary.company.name}</p>
        </div>
      </TopNavigation>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pending Approvals Alert */}
        {pendingApprovalsCount > 0 && (
          <Card className="mb-6 border-l-4 border-l-purple-500 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-purple-600" />
                  <div>
                    <h3 className="font-semibold text-base text-purple-900">
                      {pendingApprovalsCount} AI-Suggested Configuration Item{pendingApprovalsCount > 1 ? 's' : ''} Pending Review
                    </h3>
                    <p className="text-sm text-purple-700 mt-1">
                      AI analyzed client HLOs and identified additional config items not covered by the current SOW scope
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => setCIApprovalModalOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Review Suggestions
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scope Misalignment Alert (TASK 6) */}
        {summary.strategicContext?.hloAlignment && summary.strategicContext.hloAlignment.summary.uncovered > 0 && (
          <Card className="mb-6 border-l-4 border-l-red-500 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <h3 className="font-semibold text-base text-red-900">
                      Scope Misalignment Detected
                    </h3>
                    <p className="text-sm text-red-700 mt-1">
                      {summary.strategicContext.hloAlignment.summary.uncovered} client objective{summary.strategicContext.hloAlignment.summary.uncovered > 1 ? 's are' : ' is'} not covered by the current SOW scope ({summary.strategicContext.hloAlignment.score}% alignment)
                    </p>
                    <ul className="mt-2 space-y-1">
                      {summary.strategicContext.hloAlignment.hloCoverage
                        .filter(e => e.coverage === 'uncovered')
                        .slice(0, 5)
                        .map(e => (
                          <li key={e.hloId} className="text-xs text-red-800 flex items-center gap-1.5">
                            <span className="size-1.5 rounded-full bg-red-400 shrink-0" />
                            {e.title}
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <ClientDetailHeader
          company={summary.company}
          metrics={{
            totalItems: summary.totalItems,
            completedItems: summary.completedItems,
            waitingForConsultant: summary.waitingForConsultant,
            waitingForClient: summary.waitingForClient,
            blockedItems,
            progressPercentage,
            healthStatus,
            spaces: summary.spaces,
            users: summary.users,
          }}
          onBack={onBack}
          onManageUsers={() => setInviteDialogOpen(true)}
          onCompanySettings={() => setQuickCompanySettingsDialogOpen(true)}
          onShowBlockedItems={() => {
            setStatusFilter('blocked');
            setballInCourtFilter(undefined); // Clear ball in court filter
            setActiveTab('config-items');
          }}
          onShowYourActionItems={() => {
            setStatusFilter(undefined); // Clear status filter
            setballInCourtFilter('consultant'); // Filter by ballInCourt = consultant
            setActiveTab('config-items');
          }}
          onShowClientActionItems={() => {
            setStatusFilter(undefined); // Clear status filter
            setballInCourtFilter('client'); // Filter by ballInCourt = client
            setActiveTab('config-items');
          }}
          onShowScopeControls={handleShowScopeControls}
          scopeMetrics={summary.scopeMetrics}
          onViewAsClient={
            (currentUser.role === 'systemAdmin' || currentUser.role === 'system_admin') 
              ? () => setUserImpersonationDialogOpen(true) 
              : undefined
          }
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6 space-y-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sows">
              <FileText className="size-4 mr-2" />
              SOWs
              <Badge variant="secondary" className="ml-2">
                {sows.length}
              </Badge>
            </TabsTrigger>
            {/* Scope Controls - Consultants Only */}
            {(currentUser.role === 'consultant' || currentUser.role === 'cf-admin' || currentUser.role === 'cfAdmin' || currentUser.role === 'systemAdmin') && (
              <TabsTrigger value="scope-controls">
                <AlertCircle className="size-4 mr-2" />
                Scope Controls
              </TabsTrigger>
            )}
            <TabsTrigger value="config-items">
              Config Items
              <span className="ml-2 text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                {summary.totalItems}
              </span>
            </TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="documents">
              Documents
              {unviewedDocs > 0 && (
                <Badge variant="destructive" className="ml-2" aria-label={`${unviewedDocs} unread`}>{unviewedDocs}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="delivery">
              Delivery
            </TabsTrigger>
            {/* Settings - the company's AI switch, notification toggles and AI call log (consultants only) */}
            {(currentUser.role === 'consultant' || currentUser.role === 'cf-admin' || currentUser.role === 'cfAdmin' || currentUser.role === 'systemAdmin') && (
              <TabsTrigger value="settings">
                <Settings className="size-4 mr-2" />
                Settings
              </TabsTrigger>
            )}
            {/* Spaces: capability + company template (temporary dual check) */}
            {showSpacesExperience && (
              <TabsTrigger value="spaces">
                <Layers className="size-4 mr-2" />
                Spaces
                <Badge variant="secondary" className="ml-2">
                  {spaces.length}
                </Badge>
              </TabsTrigger>
            )}
          </TabsList>
          {hasMultipleEngagements && (
            <EngagementSwitcher
              engagements={sows}
              value={focusedEngagementId}
              onChange={setFocusedEngagement}
              size="sm"
            />
          )}
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            
            {/* The firm's engagement view: health, activation, objectives with Refine, risks, path
                guidance, the milestone graph and communication health (RESTORE_PLAN R2.6) */}
            <EngagementOverview
              companyId={companyId}
              companyName={summary?.name || ''}
              engagementId={focusedEngagementId}
              accessToken={accessToken}
              currentUser={currentUser}
              onStartStep={startRecommendedStep}
              onOpenSows={() => setActiveTab('sows')}
            />

            {/* Scope signals: sales notes against the signed scope */}
            {summary.strategicContext && (
              summary.strategicContext.scopeSignals ? (
                <ScopeSignalsPanel 
                    signals={summary.strategicContext.scopeSignals}
                    userRole="consultant"
                    companyId={companyId}
                    accessToken={accessToken}
                    onUpdate={handleUpdate}
                />
              ) : (
                /* Fallback to legacy card if no signals structure yet */
                <Card className="border-orange-100 bg-orange-50/30">
                    <CardContent className="pt-6">
                        <h3 className="text-base font-semibold text-orange-900 flex items-center gap-2 mb-4">
                            <AlertCircle className="size-4 text-orange-600" />
                            Sales vs. SOW Scope Analysis
                        </h3>
                        <div className="text-sm text-gray-500">
                            Legacy scope analysis active. Please refresh to load new signal structure.
                        </div>
                    </CardContent>
                </Card>
              )
            )}

            {/* What the client sees under "Where we are" on their home page */}
            <ClientStatusNoteCard companyId={companyId} accessToken={accessToken} />

            {/* Engagement insights (delivery readiness, items to clarify, scope changes, the reporting
                blueprint): consultant content that used to be step 4 of the client's onboarding
                (client walk finding L3). Loaded on request: it calls the AI. */}
            <Card data-testid="engagement-insights-card">
              <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Engagement insights</h3>
                  <p className="text-sm text-gray-500">Delivery readiness, items to clarify, scope changes and the reporting blueprint, read from the SOW and the client's goals.</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowInsights((v) => !v)}>{showInsights ? 'Hide insights' : 'Show insights'}</Button>
              </CardContent>
            </Card>
            {showInsights && <EngagementInsightsStep companyId={companyId} accessToken={accessToken} currentUser={currentUser} />}

            {/* Discovery Completeness Tracker */}
            <CompletenessTracker
              companyId={companyId}
              accessToken={accessToken}
              onRefresh={handleUpdate}
            />

            {/* Spaces Overview */}
            {showSpacesExperience && spaces.length > 0 && (
              <ClientSpacesOverview 
                spaces={spaces} 
                onViewSpace={(spaceId) => {
                  if (onNavigateToSpace) {
                    onNavigateToSpace(companyId, spaceId);
                  }
                }}
              />
            )}

            {/* Legacy Data Migration Notice - Show when config items exist but no spaces */}
            {showSpacesExperience && configItems.length > 0 && spaces.length === 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="py-6">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="size-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-base text-amber-900 mb-2">
                        Legacy Configuration Items Detected
                      </h3>
                      <p className="text-sm text-amber-800 mb-4">
                        This client has {configItems.length} config items that haven't been organized into spaces yet. 
                        The category view below shows legacy data. To enable the modern space-based workflow:
                      </p>
                      <div className="space-y-2 text-sm text-amber-800 mb-4">
                        <p>• <strong>Option 1:</strong> Have the client admin log in and complete the space planning wizard</p>
                        <p>• <strong>Option 2:</strong> Create spaces for them using the Space Management tab above</p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-amber-600 text-amber-900 hover:bg-amber-100"
                        onClick={() => {
                          setActiveTab('spaces');
                        }}
                      >
                        <Layers className="size-4 mr-2" />
                        Go to Space Management
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Category Breakdown - Legacy View */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(summary.byCategory).map(([category, data]: [string, any]) => {
                // Use averageCompleteness from backend if available, otherwise fallback to completed/total
                const categoryProgress = data.averageCompleteness !== undefined 
                  ? data.averageCompleteness
                  : (data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0);

                return (
                  <div 
                    key={category} 
                    className="border rounded-lg p-4 bg-white cursor-pointer hover:shadow-lg hover:border-blue-400 transition-all duration-200"
                    onClick={() => {
                      setSelectedCategory(category);
                      setActiveTab('config-items');
                    }}
                  >
                    <h4 className="text-base font-medium mb-3 capitalize">
                      {formatCategoryLabel(normalizeAcronyms(category))}
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total</p>
                        <p className="font-bold text-lg">{data.total}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Completed</p>
                        <p className="font-bold text-lg text-green-600">{data.completed}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">In Progress</p>
                        <p className="font-bold text-lg text-blue-600">{data.inProgress}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Your Action</p>
                        <p className="font-bold text-lg text-orange-600">{data.waitingConsultant}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">{categoryProgress}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${categoryProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recent Activity */}
            <ClientActivityTimeline
              companyId={companyId}
              accessToken={accessToken}
              limit={10}
            />
          </TabsContent>

          {/* SOWs Tab */}
          <TabsContent value="sows">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold">Statements of Work & Change Orders</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Manage engagements, deliverables, and scope changes for {summary.company.name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={() => setSOWGeneratorOpen(true)}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                    >
                      <FileText className="size-4 mr-2" />
                      Generate SOW
                    </Button>
                    <Button onClick={() => setAddSOWDialogOpen(true)} variant="outline">
                      <Plus className="size-4 mr-2" />
                      Add SOW
                    </Button>
                  </div>
                </div>

                {sowsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="size-8 animate-spin text-gray-400" />
                  </div>
                ) : sows.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="size-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium">No SOWs or Change Orders yet</p>
                    <p className="text-sm mt-1">Create your first Statement of Work to get started. You can add Change Orders to it later.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sows.map((sow) => (
                      <Card 
                        key={sow.id} 
                        className="hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => {
                          setSelectedSOWId(sow.id);
                          setSOWDetailsModalOpen(true);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-base font-semibold truncate">{sow.name}</h3>
                                <Badge 
                                  variant="outline" 
                                  className={
                                    sow.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                                    sow.status === 'pending_approval' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                    sow.status === 'draft' ? 'bg-gray-50 text-gray-700 border-gray-200' :
                                    sow.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                    'bg-red-50 text-red-700 border-red-200'
                                  }
                                >
                                  {sow.status.replace('_', ' ')}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                {sow.description}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>{sow.deliverables?.length || 0} deliverables</span>
                                <span>•</span>
                                <span>
                                  {sow.changeOrders?.length || 0} change orders
                                </span>
                                <span>•</span>
                                <span>
                                  {sow.pricing?.type === 'fixed' 
                                    ? `$${(sow.pricing.amount || 0).toLocaleString()}`
                                    : sow.pricing?.type === 'hourly'
                                    ? `$${sow.pricing.hourlyRate || 0}/hr`
                                    : sow.pricing?.type === 'hybrid'
                                    ? `$${(sow.pricing.amount || 0).toLocaleString()} + $${sow.pricing.hourlyRate || 0}/hr`
                                    : '$0'}
                                </span>
                                {formatDateRange(sow.timeline?.startDate, sow.timeline?.endDate) && (
                                  <>
                                    <span>•</span>
                                    <span>
                                      {formatDateRange(sow.timeline?.startDate, sow.timeline?.endDate)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSOWId(sow.id);
                                  setSOWVersionHistoryOpen(true);
                                }}
                              >
                                Version History
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSOWId(sow.id);
                                  setSOWDetailsModalOpen(true);
                                }}
                              >
                                View Details
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSOWId(sow.id);
                                  setShareSOWDialogOpen(true);
                                }}
                              >
                                <Share2 className="size-4 mr-2" />
                                Share
                              </Button>
                              {/* Actions dropdown: consolidates post-save SOW
                                  management (Edit / Analyze / Delete) without
                                  crowding the card. Stops click propagation so
                                  opening the menu does not also fire the card's
                                  View-Details click handler. */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    aria-label="SOW actions"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreHorizontal className="size-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {/* Edit deliberately omitted: a saved SOW is
                                      a counter-signed legal artifact (typically
                                      a PDF). Scope changes go through change
                                      orders; bad uploads go through Delete.
                                      The legacy Edit entry point in
                                      SOWDetailsModal is a separate question --
                                      see follow-up. */}
                                  {companyAiEnabled && (
                                    <DropdownMenuItem
                                      onSelect={() => {
                                        setSelectedSOWId(sow.id);
                                        setAnalyzeSOWDialogOpen(true);
                                      }}
                                    >
                                      <Sparkles className="size-4 mr-2" />
                                      Analyze with AI
                                    </DropdownMenuItem>
                                  )}
                                  {companyAiEnabled && <DropdownMenuSeparator />}
                                  <DropdownMenuItem
                                    className="text-red-600 focus:text-red-600"
                                    onSelect={() => {
                                      setDeletingSOWId(sow.id);
                                      setDeleteSOWConfirmOpen(true);
                                    }}
                                  >
                                    <Trash2 className="size-4 mr-2" />
                                    Delete SOW
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add SOW Dialog */}
            <AddSOWDialog
              open={addSOWDialogOpen}
              onClose={() => {
                setAddSOWDialogOpen(false);
                loadSOWs(); // Refresh SOWs after adding
                // Offer reconciliation against the previous engagement (spec §4.10); `sows` still lists the older ones.
                setReconcileOffer(reconcileOfferFor(createdSOW, sows));
                setCreatedSOW(null);
              }}
              accessToken={accessToken}
              consultingFirmId={currentUser.firmId || currentUser.consultingFirmId || ''}
              preselectedCompanyId={companyId}
              preselectedCompanyName={summary?.company?.name}
              onSOWCreated={(created) => {
                if (created) setCreatedSOW(created);
                loadSOWs(); // Refresh SOWs immediately when created
              }}
            />

            {reconcileOffer && (
              <ScopeReconcileDialog
                open
                onClose={() => setReconcileOffer(null)}
                companyId={companyId}
                accessToken={accessToken}
                target={reconcileOffer.target}
                engagements={reconcileOffer.engagements}
                defaultFromEngagementId={reconcileOffer.fromEngagementId}
                cancelLabel="Skip for now"
                intro={RECONCILE_AFTER_SOW_INTRO}
                onDone={() => loadSOWs()}
              />
            )}

            {/* SOW Details Modal */}
            <SOWDetailsModal
              isOpen={sowDetailsModalOpen}
              onClose={() => {
                setSOWDetailsModalOpen(false);
                setSelectedSOWId(null);
                loadSOWs(); // Refresh SOWs after viewing/editing
              }}
              accessToken={accessToken}
              sowId={selectedSOWId || ''}
              projectId={projectId}
              currentUser={currentUser}
            />
          </TabsContent>

          {/* Scope Controls Tab */}
          <TabsContent value="scope-controls">
            <ScopeControlCenter 
              companyId={companyId}
              companyName={summary.company.name}
              accessToken={accessToken}
              currentUser={currentUser}
              onNavigateToConfigItem={handleNavigateToConfigItem}
              initialFilterStatus={scopeControlFilter}
            />
          </TabsContent>

          {/* Config Items Tab */}
          <TabsContent value="config-items">
            <ClientConfigItemsTable
              items={configItems}
              spaces={spaces}
              companyId={companyId}
              companyName={summary?.name || ''}
              accessToken={accessToken}
              currentUser={currentUser}
              onUpdate={handleUpdate}
              selectedCategory={selectedCategory}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              ballInCourtFilter={ballInCourtFilter}
              onBallInCourtFilterChange={setballInCourtFilter}
              initialOpenItemId={targetConfigItemId}
              initialOpenTab={targetConfigItemTab}
              initialCellId={initialGridCellId}
              sows={sows}
              engagementId={focusedEngagementId}
              changeRequests={changeRequests}
            />
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <ClientActivityTimeline
              companyId={companyId}
              accessToken={accessToken}
              limit={50}
            />
          </TabsContent>

          {/* Documents Tab: the company's documents, with what the viewer has not opened yet (the old ConsultantPanel's Documents tab) */}
          <TabsContent value="documents">
            <CompanyDocuments
              accessToken={accessToken}
              companyId={companyId}
              companyName={summary.company.name}
              currentUser={currentUser}
              onUnviewedCountChange={setUnviewedDocs}
            />
          </TabsContent>

          {/* Delivery Workspace Tab */}
          <TabsContent value="delivery" className="space-y-6">
            {/* Step 4 of the simplification plan (ledger L28): the build backlog, with the status control. */}
            <BuildBacklog companyId={companyId} accessToken={accessToken} currentUser={currentUser} isConsultant />
            {/* Step 4 of the simplification plan (ledger L29, L30): export per CI as import-ready
                files, and the implementation documentation, both on the live routes. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="deliver-tools">
              <Card>
                <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Export</h3>
                    <p className="text-sm text-gray-500">This client's configuration as import-ready files for Jira, ServiceNow, Zendesk, Ivanti, BMC or Freshservice, with the implementation guide.</p>
                  </div>
                  <Button size="sm" onClick={() => setShowExport(true)}>Export…</Button>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Implementation documentation</h3>
                    <p className="text-sm text-gray-500">The implementation guide, the checklist and the configuration export for the handoff.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowDocs((v) => !v)}>{showDocs ? 'Hide documentation' : 'Show documentation'}</Button>
                </CardContent>
              </Card>
            </div>
            {showDocs && <ImplementationDocumentation companyId={companyId} accessToken={accessToken} companyName={summary?.company?.name ?? ''} />}
            <ExportWizard isOpen={showExport} companyId={companyId} accessToken={accessToken} onClose={() => setShowExport(false)} />

            {/* Step 4, last pieces: wins and kudos for the engagement (ledger L51), and the Jira
                backlog as a manual export for Atlassian firms (L28, L31: the push waits with Forge). */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="deliver-tools-2">
              <Card>
                <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Wins and kudos</h3>
                    <p className="text-sm text-gray-500">What went well on this engagement, in the client's words, and whether they will say so publicly.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowWins((v) => !v)} disabled={!focusedEngagementId}>{showWins ? 'Hide wins' : 'Show wins'}</Button>
                </CardContent>
              </Card>
              {showSpacesExperience && (
                <Card>
                  <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Jira backlog</h3>
                      <p className="text-sm text-gray-500">Generate the build backlog in the client's Jira from the locked configuration: a manual export with your own credentials.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowJira((v) => !v)}>{showJira ? 'Hide Jira backlog' : 'Open Jira backlog'}</Button>
                  </CardContent>
                </Card>
              )}
              {/* RESTORE_PLAN R1.8: the old Advanced Configuration page's Assets entry */}
              {showSpacesExperience && (
                <Card>
                  <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Assets and CMDB</h3>
                      <p className="text-sm text-gray-500">Install a CMDB starter template for this client and see the asset schemas installed, with each object type's discovery mapping.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowAssets((v) => !v)}>{showAssets ? 'Hide assets' : 'Open assets'}</Button>
                  </CardContent>
                </Card>
              )}
            </div>
            {showWins && focusedEngagementId && <WinsKudosTab engagementId={focusedEngagementId} companyId={companyId} accessToken={accessToken} />}
            {showJira && showSpacesExperience && <JiraBacklogGenerator companyId={companyId} companyName={summary?.company?.name ?? ''} accessToken={accessToken} />}
            {showAssets && showSpacesExperience && (
              <Card>
                <CardContent className="pt-6">
                  <AssetConfiguration companyId={companyId} accessToken={accessToken} />
                </CardContent>
              </Card>
            )}
            <EngagementWorkspace
              companyId={companyId}
              accessToken={accessToken}
              currentUser={currentUser}
            />
          </TabsContent>

          {/* Settings Tab: company AI switch, notification toggles, AI call log */}
          <TabsContent value="settings">
            <CompanySettings
              accessToken={accessToken}
              companyId={companyId}
              companyName={summary.company.name}
              currentUser={currentUser}
            />
          </TabsContent>

          {/* Spaces Tab */}
          <TabsContent value="spaces">
            {showSpacesExperience ? (
              <SpaceManagementPanel
                accessToken={accessToken}
                consultantId={currentUser.id}
                companyId={companyId}
                companyName={summary.company.name}
                currentUser={currentUser}
                onUpdate={() => {
                  loadClientSummary();
                  loadConfigItems();
                }}
                onNavigateToSpace={onNavigateToSpace}
              />
            ) : null}
          </TabsContent>
        </Tabs>
      </main>

      <UserImpersonationDialog
        isOpen={userImpersonationDialogOpen}
        onClose={() => setUserImpersonationDialogOpen(false)}
        companyId={companyId}
        companyName={summary?.company?.name || ''}
        accessToken={accessToken}
      />

      {/* CI Approval Modal */}
      {ciApprovalModalOpen && pendingApprovals && pendingApprovalsCount > 0 && (
        <CIApprovalModal
          companyId={companyId}
          companyName={summary.company.name}
          pendingCIs={pendingApprovals.suggestedCIs || []}
          accessToken={accessToken}
          onClose={() => {
            setCIApprovalModalOpen(false);
          }}
          onComplete={() => {
            loadConfigItems();
            loadPendingApprovals();
            toast.success('CI approvals processed successfully');
          }}
        />
      )}

      <InviteClientUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        companyId={companyId}
        companyName={summary.company.name}
        accessToken={accessToken}
      />

      {/* Quick Company Settings Dialog */}
      <QuickCompanySettingsDialog
        open={quickCompanySettingsDialogOpen}
        onClose={() => {
          setQuickCompanySettingsDialogOpen(false);
        }}
        accessToken={accessToken}
        companyId={companyId}
        companyName={summary.company.name}
        onUpdated={() => {
          loadClientSummary();
          toast.success('Company settings updated successfully');
        }}
      />

      {/* SOW Generator */}
      <SOWGenerator
        open={sowGeneratorOpen}
        onOpenChange={setSOWGeneratorOpen}
        client={{
          id: companyId,
          name: summary.company.name,
          assignedTemplateIds: companyTemplate ? [companyTemplate] : [], // Would be loaded from company data
        } as any}
        accessToken={accessToken}
        consultingFirm={{
          id: currentUser.firmId || currentUser.consultingFirmId,
          name: currentUser.firmName || 'Your Firm',
          defaultHourlyRate: 150,
        } as any}
        onNavigateToEngagementCatalog={() => {
          // Open the firm's Engagement Catalog here, inside the client's context; the generator reopens (and
          // reloads engagement types) when the catalog closes.
          setSOWGeneratorOpen(false);
          setEngagementCatalogOpen(true);
        }}
      />

      <Dialog
        open={engagementCatalogOpen}
        onOpenChange={(open) => {
          setEngagementCatalogOpen(open);
          if (!open) setSOWGeneratorOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Engagement Catalog</DialogTitle>
            <DialogDescription>Engagement types are firm-wide templates. Create one here, then close this to continue the SOW.</DialogDescription>
          </DialogHeader>
          <EngagementCatalog firmId={currentUser.firmId || currentUser.consultingFirmId} accessToken={accessToken} />
        </DialogContent>
      </Dialog>

      {/* SOW Version History */}
      {selectedSOWId && (
        <SOWVersionHistory
          open={sowVersionHistoryOpen}
          onOpenChange={setSOWVersionHistoryOpen}
          sowId={selectedSOWId}
          firmId={currentUser.firmId || currentUser.consultingFirmId || ''}
          accessToken={accessToken}
          onVersionRestored={() => {
            loadSOWs();
            toast.success('SOW version restored successfully');
          }}
        />
      )}

      {/* Share SOW Dialog */}
      {selectedSOWId && (
        <ShareSOWDialog
          open={shareSOWDialogOpen}
          onOpenChange={setShareSOWDialogOpen}
          sowId={selectedSOWId}
          sowTitle={sows.find(s => s.id === selectedSOWId)?.name || 'SOW'}
          firmId={currentUser.firmId || currentUser.consultingFirmId || ''}
          accessToken={accessToken}
        />
      )}

      {/* Edit SOW Dialog REMOVED: executed SOWs are legal contracts.
          The ability to edit scope/pricing/timeline of a saved SOW has
          been retired. Use a change order to amend, or delete and
          re-upload if the wrong file was attached. The PUT
          /consultant/sows/:id route enforces the same constraint server
          side via FROZEN_STATUSES. */}

      {/* Analyze SOW Dialog -- only mounts when a SOW is targeted + AI is
          enabled. Gating render (not just the menu item) keeps us from
          holding state for a flow the user can't trigger, and means any
          runaway fetch inside the dialog can't fire off for an AI-disabled
          company. */}
      {selectedSOWId && companyAiEnabled && (
        <AnalyzeSOWDialog
          open={analyzeSOWDialogOpen}
          onClose={() => {
            setAnalyzeSOWDialogOpen(false);
            setSelectedSOWId(null);
          }}
          accessToken={accessToken}
          companyId={companyId}
          sowId={selectedSOWId}
          sowName={sows.find((s) => s.id === selectedSOWId)?.name}
          onApplied={() => {
            // Refresh everything Analyze can change:
            //   - configItems: new/updated overrides from the analyzer
            //   - sows: scopeDescription / sowFileName updated by upload-sow
            //   - clientSummary: hloAlignment is recomputed inside the
            //     /client/recommendations response and surfaced via
            //     summary.strategicContext.hloAlignment, so the
            //     "Scope Misalignment Detected" banner only re-renders
            //     when we re-pull the summary.
            loadConfigItems();
            loadSOWs();
            loadClientSummary();
          }}
        />
      )}

      {/* Delete SOW Confirmation. Two-step to prevent accidental destruction
          of engagement records. On confirm, DELETE /consultant/sows/:sowId
          (which now also removes the Postgres row via deleteEngagementFromPg),
          then refresh the list. */}
      <AlertDialog
        open={deleteSOWConfirmOpen}
        onOpenChange={(isOpen) => {
          setDeleteSOWConfirmOpen(isOpen);
          if (!isOpen) setDeletingSOWId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this SOW?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the SOW and its engagement record. Any
              associated change orders, version history, and applied config
              items remain, but this SOW will no longer appear on the client
              page. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingSOW}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteSOWConfirmed();
              }}
              disabled={deletingSOW}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingSOW ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>Delete SOW</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}